import { useState } from 'react';
import { formaterMontant, formaterDate } from '../lib/format.js';

/**
 * Quelques chiffres en tête du tableau de bord.
 *
 * Tous calculés depuis la liste déjà chargée : aucun appel supplémentaire, et
 * rien à maintenir côté serveur pour des valeurs qui se déduisent de ce qu'on
 * a sous la main.
 */

const TOUTES = 'toutes';

/**
 * Année à laquelle rattacher un voyage.
 *
 * La date de départ quand elle existe. Sinon on cherche une année dans la
 * période libre — « printemps 2027 » en contient une, « cet été » non. Un
 * voyage qui n'en a pas n'appartient à aucune année : il n'est compté que dans
 * la vue d'ensemble, et on le signale plutôt que de le faire disparaître sans
 * explication.
 */
export function anneeDuVoyage(voyage) {
  if (voyage.dateMode === 'fixed_dates' && voyage.startDate) {
    return new Date(voyage.startDate).getFullYear();
  }
  const trouvee = voyage.period?.match(/\b(20\d{2})\b/);
  return trouvee ? Number(trouvee[1]) : null;
}

/** Années proposées au filtre : celles des voyages, plus l'année en cours. */
export function anneesDisponibles(voyages, aujourdhui = new Date()) {
  const annees = new Set(voyages.map(anneeDuVoyage).filter((a) => a !== null));
  // L'année en cours est toujours offerte : c'est la valeur par défaut, elle
  // ne peut pas être absente de sa propre liste.
  annees.add(aujourdhui.getFullYear());
  return [...annees].sort((a, b) => b - a);
}

function compter(voyages) {
  const planifies = voyages.filter((v) => v.status === 'planned');
  const archives = voyages.filter((v) => v.status === 'archived');

  const aujourdhui = new Date();
  aujourdhui.setHours(0, 0, 0, 0);

  const dates = voyages
    .filter((v) => v.startDate)
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  // Un départ à venir s'il en reste un ; sinon le dernier parti. Sur une année
  // révolue, « prochain départ » n'aurait rien à montrer, et un tiret à la
  // place d'un chiffre n'apprend rien.
  const aVenir = dates.find((v) => new Date(v.startDate) >= aujourdhui) ?? null;
  const passe = aVenir ? null : dates[dates.length - 1] ?? null;

  return {
    planifies,
    archives,
    depart: aVenir ?? passe,
    departPasse: !aVenir && Boolean(passe),
    joursCumules: voyages.reduce((total, v) => total + (v.durationDays ?? 0), 0),
    // Les budgets sont en centimes ; on additionne des entiers, on ne convertit
    // qu'à l'affichage.
    budgetCumule: voyages.reduce((total, v) => total + (v.budget.amount ?? 0), 0),
  };
}

function Indicateur({ valeur, libelle, precision }) {
  return (
    <li className="indicateur">
      <span className="indicateur__valeur">{valeur}</span>
      <span className="indicateur__libelle">{libelle}</span>
      {precision && <span className="indicateur__precision">{precision}</span>}
    </li>
  );
}

export function Indicateurs({ voyages }) {
  const annees = anneesDisponibles(voyages);
  const [annee, setAnnee] = useState(() => String(new Date().getFullYear()));

  if (voyages.length === 0) return null;

  const toutes = annee === TOUTES;
  const retenus = toutes
    ? voyages
    : voyages.filter((v) => anneeDuVoyage(v) === Number(annee));

  // Sans date ni année dans la période : invisibles dès qu'on filtre.
  const sansAnnee = voyages.filter((v) => anneeDuVoyage(v) === null).length;

  const { planifies, archives, depart, departPasse, joursCumules, budgetCumule } =
    compter(retenus);
  const enPreparation = retenus.length - planifies.length - archives.length;

  const filtre = (
    <div className="indicateurs__entete">
      <h2 className="indicateurs__titre">Vos voyages en chiffres</h2>
      <div className="indicateurs__filtre">
        <label htmlFor="annee">Année</label>
        <select id="annee" value={annee} onChange={(e) => setAnnee(e.target.value)}>
          {annees.map((valeur) => (
            <option key={valeur} value={String(valeur)}>
              {valeur}
            </option>
          ))}
          <option value={TOUTES}>Toutes</option>
        </select>
      </div>
    </div>
  );

  if (retenus.length === 0) {
    return (
      <section className="indicateurs-bloc">
        {filtre}
        <p className="aide">
          Aucun voyage en {annee}.
          {sansAnnee > 0
            && ` ${sansAnnee} voyage${sansAnnee > 1 ? 's n’ont' : ' n’a'} pas encore de date`
              + ' : choisissez « Toutes » pour l’y retrouver.'}
        </p>
      </section>
    );
  }

  const etendue = toutes ? 'toutes années' : annee;

  return (
    <section className="indicateurs-bloc">
      {filtre}

      <ul className="indicateurs">
        <Indicateur
          valeur={planifies.length}
          libelle={planifies.length > 1 ? 'Voyages planifiés' : 'Voyage planifié'}
          precision={
            [
              enPreparation > 0 ? `${enPreparation} en préparation` : null,
              archives.length > 0
                ? `${archives.length} archivé${archives.length > 1 ? 's' : ''}`
                : null,
            ]
              .filter(Boolean)
              .join(', ') || 'tous vos voyages sont prêts'
          }
        />
        <Indicateur
          valeur={joursCumules}
          libelle={joursCumules > 1 ? 'Jours de voyage' : 'Jour de voyage'}
          precision={`sur ${retenus.length} voyage${retenus.length > 1 ? 's' : ''}`}
        />
        <Indicateur
          valeur={depart ? formaterDate(depart.startDate).replace(/ \d{4}$/, '') : '—'}
          libelle={departPasse ? 'Dernier départ' : 'Prochain départ'}
          precision={depart ? depart.destination.label : 'aucune date fixée'}
        />
        <Indicateur
          valeur={budgetCumule > 0 ? formaterMontant(budgetCumule) : '—'}
          libelle="Budget cumulé"
          precision={budgetCumule > 0 ? `indicatif, ${etendue}` : 'aucun budget renseigné'}
        />
      </ul>

      {!toutes && sansAnnee > 0 && (
        <p className="aide">
          {sansAnnee} voyage{sansAnnee > 1 ? 's' : ''} sans date connue
          {sansAnnee > 1 ? ' ne sont' : ' n’est'} compté{sansAnnee > 1 ? 's' : ''} dans aucune
          année. Choisissez « Toutes » pour {sansAnnee > 1 ? 'les ' : 'l’'}inclure.
        </p>
      )}
    </section>
  );
}
